import React, { useState, useRef, useEffect } from 'react';
import AutoplayModal from './AutoplayModal';
import SettingsModal from '../Settings/SettingsModal';
import { getAudioPath } from '../../../utils/storyUtils';
import { getAssetUrl } from '../../../utils/assetUtils';
import { PATHS, STORAGE_KEYS } from '../../../constants';
import { Button, Text } from "@geist-ui/core";
import {
  PlayCircle,
  RotateCcw,
  Save,
  ArrowLeftCircle,
  Settings,
  Search,
  PauseCircle,
  StopCircle,
  X,
  Bookmark
} from "@geist-ui/icons";
import { 
  trackAudioEvent,
  trackSettingChange,
  trackUserInteraction,
  trackUserPreference,
  logEvent
} from '../../../config/analytics';
import { useAudio } from '../../../context/AudioContext';

const SideBar = ({
	loadScene,
	currentChapter,
	goBack,
	handleSave,
	handleResume,
	handleFontSizeChange,
	handleColumns,
	isTwoColumns,
	jumpToSection,
	savedSceneId,
	currentSceneId,
	bookTitle,
	sidebarShow,
	toggleSidebar,
	bookCover,
	fontSize,
	hasAudio,
	bookId,
	storyData
}) => {
	const [sectionNumber, setSectionNumber] = useState('');
	const { playbackRate, setPlaybackRate } = useAudio();
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [volume, setVolume] = useState(0.5);
	const [isAutoplay, setIsAutoplay] = useState(false);
	const [isBackgroundMusicPlaying, setIsBackgroundMusicPlaying] = useState(false);
	const [backgroundMusicVolume, setBackgroundMusicVolume] = useState(0.35);
	const [isAudioLoaded, setIsAudioLoaded] = useState(false);
	const audioRef = useRef(null);
	const backgroundMusicRef = useRef(null);
	const [showAutoplayModal, setShowAutoplayModal] = useState(true);
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [voicePreference, setVoicePreference] = useState(() => {
		return localStorage.getItem(STORAGE_KEYS.VOICE_PREFERENCE) || 'male';
	});
	const [bookmarkedSceneId, setBookmarkedSceneId] = useState(null);
	const [coverImageSrc, setCoverImageSrc] = useState(getAssetUrl(bookCover || "/assets/book1.jpg", { forceWebP: true }));

	const getMaxSectionNumber = () => {
		if (!storyData) return 999;
		
		let maxSection = 0;
		Object.values(storyData).forEach(chapter => {
			Object.keys(chapter).forEach(sceneId => {
				const sectionNum = parseInt(sceneId, 10);
				if (!isNaN(sectionNum) && sectionNum > maxSection) {
					maxSection = sectionNum;
				}
			});
		});
		
		return maxSection;
	};
	
	const maxSection = getMaxSectionNumber();

	useEffect(() => {
		if (!currentSceneId || !currentChapter || !hasAudio) return;

		const audioPath = getAudioPath(
			bookId,
			currentChapter,
			currentSceneId,
			voicePreference
		);

		if (!audioPath) return;

		if (audioRef.current) {
			const wasPlaying = !audioRef.current.paused;
			setIsAudioLoaded(false);
			audioRef.current.src = audioPath;
			audioRef.current.load();
			audioRef.current.volume = volume;
			setCurrentTime(0);
			setDuration(0);

			const handleCanPlay = () => {
				if (isAutoplay && hasAudio) {
					audioRef.current.play()
						.then(() => setIsPlaying(true))
						.catch(console.error);
				}
			};

			audioRef.current.addEventListener('canplaythrough', handleCanPlay, { once: true });

			return () => {
				if (audioRef.current) {
					audioRef.current.removeEventListener('canplaythrough', handleCanPlay);
				}
			};
		}
	}, [currentSceneId, currentChapter, voicePreference, isAutoplay, hasAudio, bookId]);

	useEffect(() => {
		const savedAutoplayPref = localStorage.getItem('autoplayPref');
		if (savedAutoplayPref) {
			setShowAutoplayModal(false);
			setIsAutoplay(savedAutoplayPref === 'true');
		}

		const cleanupStorage = () => {
			localStorage.removeItem('autoplayPref');
		};

		window.addEventListener('beforeunload', cleanupStorage);
		return () => window.removeEventListener('beforeunload', cleanupStorage);
	}, []);

	useEffect(() => {
		try {
			const bookmarkStr = localStorage.getItem('bookmark');
			if (bookmarkStr) {
				const bookmark = JSON.parse(bookmarkStr);
				if (bookmark.bookId === bookId) {
					setBookmarkedSceneId(bookmark.sceneId);
				} else {
					setBookmarkedSceneId(null);
				}
			}
		} catch (error) {
			console.error('Error loading bookmark:', error);
		}
	}, [bookId]);

	const handleAutoplayChoice = (choice) => {
		setIsAutoplay(choice);
		setShowAutoplayModal(false);
		localStorage.setItem('autoplayPref', choice.toString());
		
		if (choice && audioRef.current && isAudioLoaded && hasAudio) {  // Only play if audio exists
			audioRef.current.play()
				.then(() => {
					setIsPlaying(true);
				})
				.catch(console.error);
		}
	};

	const startAudio = () => {
		if (!audioRef.current || !isAudioLoaded) return;
		const playPromise = audioRef.current.play();
		if (playPromise !== undefined) {
			playPromise
				.then(() => setIsPlaying(true))
				.catch(err => {
					console.warn('Audio playback failed:', err);
					setIsPlaying(false);
				});
		}
	};

	useEffect(() => {
		if (backgroundMusicRef.current) {
			backgroundMusicRef.current.volume = backgroundMusicVolume;
			backgroundMusicRef.current.loop = true;
		}
	}, [backgroundMusicVolume]);

	useEffect(() => {
		if (audioRef.current) {
			audioRef.current.volume = volume;
		}
	}, [volume]);

	const handleJump = () => {
		trackUserInteraction('Jump to Section', `Section: ${sectionNumber}`);
		if (sectionNumber) {
			const sectionNum = parseInt(sectionNumber, 10);
			if (!isNaN(sectionNum) && sectionNum >= 0 && sectionNum <= maxSection) {
				jumpToSection(sectionNum.toString());
				setSectionNumber('');
			} else if (sectionNum > maxSection) {
				setSectionNumber('');
			}
		}
	};

	const handleVolumeChange = (e) => {
		const newVolume = parseFloat(e.target.value);
		trackSettingChange('Volume', newVolume);
		setVolume(newVolume);
		if (audioRef.current) {
			audioRef.current.volume = newVolume;
		}
	};

	const toggleAutoplay = () => {
		setIsAutoplay(prev => {
			const newState = !prev;
			if (newState && audioRef.current && !isPlaying) {
				startAudio();
			}
			return newState;
		});
	};

	const toggleBackgroundMusic = () => {
		trackAudioEvent(
			isBackgroundMusicPlaying ? 'Background Music Stop' : 'Background Music Start',
			currentSceneId,
			currentChapter
		);
		if (backgroundMusicRef.current) {
			if (isBackgroundMusicPlaying) {
				backgroundMusicRef.current.pause();
			} else {
				backgroundMusicRef.current.play().catch(console.error);
			}
			setIsBackgroundMusicPlaying(!isBackgroundMusicPlaying);
		}
	};

	const handleBackgroundMusicVolumeChange = (e) => {
		const newVolume = parseFloat(e.target.value);
		trackSettingChange('Background Music Volume', newVolume);
		setBackgroundMusicVolume(newVolume);
		if (backgroundMusicRef.current) {
			backgroundMusicRef.current.volume = newVolume;
		}
	};

	const openSettings = () => {
		setIsSettingsOpen(true);
	};

	const closeSettings = () => {
		setIsSettingsOpen(false);
	};

	const loadSidebarAudio = (sceneId, chapter) => {
		if (!sceneId || !chapter || !hasAudio) return;
		
		trackUserPreference('Voice', voicePreference);
		logEvent('Settings', 'Voice Change', voicePreference);
		
		const audioPath = getAudioPath(
			bookId,
			chapter,
			sceneId,
			voicePreference
		);
		
		if (audioRef.current && audioPath) {
			const wasPlaying = !audioRef.current.paused;
			const currentTime = audioRef.current.currentTime;
			
			setIsAudioLoaded(false);
			audioRef.current.src = audioPath;
			audioRef.current.load();
			audioRef.current.volume = volume;
			
			audioRef.current.currentTime = currentTime;
			if (wasPlaying) {
				audioRef.current.play().catch(console.error);
			}
		}
	};
	
	const handleVoiceChange = (newVoice) => {
		setVoicePreference(newVoice);
		localStorage.setItem(STORAGE_KEYS.VOICE_PREFERENCE, newVoice);
		loadSidebarAudio(currentSceneId, currentChapter);
	};

	const handleCanPlayThrough = () => {
		setIsAudioLoaded(true);
	};

	const handlePlayPause = () => {
		if (audioRef.current) {
			const action = isPlaying ? 'Pause' : 'Play';
			trackAudioEvent(action, currentSceneId, currentChapter);
			logEvent('Audio', action, `${currentChapter} - Scene ${currentSceneId}`, currentTime);
			if (isPlaying) {
				audioRef.current.pause();
				setIsPlaying(false);
			} else if (isAudioLoaded) {
				startAudio();
			}
		}
	};

	const handleStop = () => {
		if (audioRef.current) {
			trackAudioEvent('Stop', currentSceneId, currentChapter);
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
			setIsPlaying(false);
			setCurrentTime(0);
		}
	};

	const handleTimeUpdate = () => {
		if (audioRef.current) {
			const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
			const slider = document.querySelector('.progress-slider');
			if (slider) {
				slider.style.setProperty('--progress', `${progress}%`);
			}
			setCurrentTime(audioRef.current.currentTime);
			setDuration(audioRef.current.duration);
		}
	};

	const handleSeek = (e) => {
		const seekTime = parseFloat(e.target.value);
		if (audioRef.current) {
			audioRef.current.currentTime = seekTime;
			setCurrentTime(seekTime);
		}
	};

	const formatTime = (time) => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
	};

	useEffect(() => {
		if (audioRef.current) {
			audioRef.current.playbackRate = playbackRate;
		}
	}, [playbackRate, audioRef.current]);

	const handleBookmark = () => {
		if (!currentSceneId || !currentChapter) return;
		try {
			const bookmark = {
				bookId,
				chapter: currentChapter,
				sceneId: currentSceneId
			};
			localStorage.setItem('bookmark', JSON.stringify(bookmark));
			setBookmarkedSceneId(currentSceneId);
			trackUserInteraction('Bookmark', `${currentChapter} - Scene ${currentSceneId}`);
		} catch (error) {
			console.error('Error saving bookmark:', error);
		}
	};

	const loadBookmark = () => {
		try {
			const bookmarkStr = localStorage.getItem('bookmark');
			if (!bookmarkStr) return;
			const bookmark = JSON.parse(bookmarkStr);
			if (bookmark.bookId !== bookId) {
				console.warn('Bookmark is for a different book');
				return;
			}
			trackUserInteraction('Load Bookmark', `${bookmark.chapter} - Scene ${bookmark.sceneId}`);
			jumpToSection(bookmark.sceneId);
		} catch (error) {
			console.error('Error loading bookmark:', error);
		}
	};

	// Update image source when book cover changes
	useEffect(() => {
		setCoverImageSrc(getAssetUrl(bookCover || "/assets/book1.jpg", { forceWebP: true }));
	}, [bookCover]);
	
	// Handle image load errors
	const handleCoverImageError = () => {
		console.error("Failed to load book cover image:", bookCover);
		// Try loading without WebP as fallback
		setCoverImageSrc(getAssetUrl(bookCover || "/assets/book1.jpg", { forceWebP: false }));
	};

	return (		
    <aside className={`sidebar ${sidebarShow ? "show" : ""}`}>
      {showAutoplayModal && <AutoplayModal onChoice={handleAutoplayChoice} />}

      <div onClick={toggleSidebar} className="hide-sidebar">
        <X/>
      </div>

      <div className="sidebar-content">
        <img
          className="book-image"
          src={coverImageSrc}
          alt={bookTitle || "Book"}
          onError={handleCoverImageError}
        />

        {bookTitle && <Text h1>{bookTitle}</Text>}
        {currentChapter && <Text p>{currentChapter}</Text>}

        <Button icon={<PlayCircle />} onClick={loadScene} className="theme-button theme-button--w100">
          <Text span>Start / Restart</Text>
        </Button>

        <Button icon={<ArrowLeftCircle />} onClick={goBack} className="theme-button theme-button--w100">
          <Text span>Back</Text>
        </Button>

        <Button icon={<Bookmark />} onClick={handleBookmark} className="theme-button theme-button--w100">
          <Text span>Bookmark</Text>
        </Button>

        {bookmarkedSceneId && (
          <Button icon={<RotateCcw />} onClick={loadBookmark} className="theme-button theme-button--w100">
            <Text span>Load Bookmark ({bookmarkedSceneId})</Text>
          </Button>
        )}

        {hasAudio && (
          <div className="audio-controls">
            <Button
              icon={isPlaying ? <PauseCircle /> : <PlayCircle />}
              onClick={handlePlayPause}
              auto
            ></Button>

            <Button icon={<StopCircle />} onClick={handleStop} auto></Button>

            <div className="audio-progress">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                className="progress-slider"
              />
            </div>

            <div className="time-display">
              {duration ? formatTime(currentTime) : "--:--"}
            </div>
          </div>
        )}

        <div
          className="jump-to-section"
          style={{ display: "flex", alignItems: "center", gap: "0px" }}
        >
          <input
            type="number"
            value={sectionNumber}
            onChange={e => {
              const value = e.target.value;
              if (value === '' || (parseInt(value, 10) >= 0 && parseInt(value, 10) <= maxSection)) {
                setSectionNumber(value);
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleJump();
              }
            }}
            min="0"
            max={maxSection}
            placeholder="Enter section"
            className="section-input"
          />
          <Button icon={<Search />} onClick={handleJump} className="theme-button">
            <Text span></Text>
          </Button>
        </div>

        <Button icon={<Settings />} onClick={openSettings} className="theme-button theme-button--w100">
          <Text span>Settings</Text>
        </Button>

        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={closeSettings}
          volume={volume}
          onVolumeChange={handleVolumeChange}
          isPlaying={isPlaying}
          handlePlayPause={handlePlayPause}
          handleStop={handleStop}
          currentTime={currentTime}
          duration={duration}
          handleSeek={handleSeek}
          isAutoplay={isAutoplay}
          toggleAutoplay={toggleAutoplay}
          isBackgroundMusicPlaying={isBackgroundMusicPlaying}
          toggleBackgroundMusic={toggleBackgroundMusic}
          backgroundMusicVolume={backgroundMusicVolume}
          handleBackgroundMusicVolumeChange={handleBackgroundMusicVolumeChange}
          fontSize={fontSize}
          handleFontSizeChange={handleFontSizeChange}
          voicePreference={voicePreference}
          handleVoiceChange={handleVoiceChange}
          handleColumns={handleColumns}
          isTwoColumns={isTwoColumns}
          playbackRate={playbackRate}
          handlePlaybackRateChange={setPlaybackRate}
        />

        <audio
          ref={audioRef}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
          onCanPlayThrough={handleCanPlayThrough}
          preload="auto"
        />

        <audio
          ref={backgroundMusicRef}
          src="https://om-assets.fra1.cdn.digitaloceanspaces.com/assets/audio/background_music/The%20Wolves%20of%20Langston%20Ambient%20(mp3,%20Loopable).mp3"
          loop
        />
      </div>
    </aside>
  );
};

export default SideBar;
